import { unstable_useId as useId } from '@mui/utils';
import {
 FormControl,
 ListItemIcon,
 MenuItem,
 Select,
 SelectChangeEvent,
 useTheme,
} from '@mui/material';
import {
 GridColumnMenuItemProps,
 useGridApiContext,
 useGridRootProps,
 useGridSelector,
} from '@mui/x-data-grid-premium';
import { gridAggregationModelSelector } from '@mui/x-data-grid-premium/hooks/features/aggregation';
import {
 canColumnHaveAggregationFunction,
 getAggregationFunctionLabel,
 getAvailableAggregationFunctions,
} from '@mui/x-data-grid-premium/hooks/features/aggregation/gridAggregationUtils';
import { useMemo } from 'react';
import IconAggregate from '@assets/icons/aggregate.svg?react';
const ColumnMenuAggregationItem = ({ colDef }: GridColumnMenuItemProps) => {
 const apiRef = useGridApiContext();
 const th = useTheme();
 const rootProps = useGridRootProps();
 const id = useId();

 const aggregationModel = useGridSelector(apiRef, gridAggregationModelSelector);

 const availableAggregationFunctions = useMemo(
  () =>
   getAvailableAggregationFunctions({
    aggregationFunctions: rootProps.aggregationFunctions,
    colDef,
   }),
  [colDef, rootProps.aggregationFunctions],
 );

 const selectedAggregationRule = useMemo(() => {
  if (!colDef || !aggregationModel[colDef.field]) return '';

  const aggregationFunctionName = aggregationModel[colDef.field];
  if (
   canColumnHaveAggregationFunction({
    colDef,
    aggregationFunctionName,
    aggregationFunction:
     rootProps.aggregationFunctions[aggregationFunctionName],
   })
  ) {
   return aggregationFunctionName;
  }
  return '';
 }, [rootProps.aggregationFunctions, aggregationModel, colDef]);

 const handleAggregationItemChange = (event: SelectChangeEvent<string>) => {
  const newAggregationItem = event.target?.value || undefined;
  const currentModel = gridAggregationModelSelector(apiRef);
  const { [colDef.field]: _, ...otherColumnItems } = currentModel;
  const newModel =
   newAggregationItem == null
    ? otherColumnItems
    : { ...otherColumnItems, [colDef.field]: newAggregationItem };
  apiRef.current.setAggregationModel(newModel);
  apiRef.current.hideColumnMenu();
 };

 const label = apiRef.current.getLocaleText('aggregationMenuItemHeader');

 return (
  <MenuItem disableRipple>
   <ListItemIcon>
    <IconAggregate style={{ color: th.palette.kemek.darkestGray }} />
   </ListItemIcon>
   <FormControl size="small" sx={{}}>
    <Select
     id={`${id}-input`}
     value={selectedAggregationRule}
     onChange={handleAggregationItemChange}
     onBlur={(e) => e.stopPropagation()}
     displayEmpty
     variant="standard"
     sx={{
      '& .MuiInput-input': {
       paddingRight: '32px !important',
      },
      height: 23,
      fontSize: 14,
      // minWidth: 150,
      '&:before': { display: 'none' },
      '&:after': { display: 'none' },
      '.MuiSelect-select': { pb: 0 },
     }}
    >
     <MenuItem value="">
      <em>{label}</em>
     </MenuItem>
     {availableAggregationFunctions.map((aggFunc) => (
      <MenuItem key={aggFunc} value={aggFunc}>
       {getAggregationFunctionLabel({
        apiRef,
        aggregationRule: {
         aggregationFunctionName: aggFunc,
         aggregationFunction: rootProps.aggregationFunctions[aggFunc],
        },
       })}
      </MenuItem>
     ))}
    </Select>
   </FormControl>
  </MenuItem>
 );
};

export default ColumnMenuAggregationItem;
